import {useRouteLoaderData} from '@remix-run/react'
import clsx from 'clsx'
import React, {
  type HTMLAttributes,
  type SyntheticEvent,
  Fragment,
  useRef,
  useState,
  type FunctionComponent,
  useEffect,
} from 'react'
import {useInView} from 'react-intersection-observer'
import {useReportCustomEvent, useReportWidgetClick, useReportWidgetSeen} from '~/hooks/analytic-hooks'
import {AmazonLogoIcon} from '~/icons/amazon-logo-icon'
import {IconChevronRightThick} from '~/icons/brg-icon-chevron-right-thick'
import {getTranslation} from '~/localization/translation'
import type {CustomerHighlightsSpecProps, RootLoaderResponse} from '~/types'
import {FeedbackStar} from '~/icons/feedback-star'
import {ThumbUpIcon} from '~/icons/thumb-up-icon'
import {getDomainConfig} from '~/domains'

const HIGHLIGHTS_ATTRIBUTE_COLORS = [
  'bg-[#9A3412] text-white',
  'bg-[#1E40AF] text-white',
  'bg-[#166534] text-white',
  'bg-[#075985] text-white',
  'bg-[#9F1239] text-white',
  'bg-[#6B21A8] text-white',
]

interface CustomerHighlightsBeyondProps extends HTMLAttributes<HTMLDivElement> {
  redirectUrl: string
  handleCardClick: (e: SyntheticEvent) => void
  cardPlacement: number
  ratingCount?: number | undefined
  score: string
  showBottomCta: boolean
  chosenBy?: number
  brand?: string
  customerHighlights?: CustomerHighlightsSpecProps[] | undefined
}
export interface InViewRefCardProps {
  firstItemInView: boolean
  lastItemInView: boolean
}

const CustomerHighlightsBeyond: FunctionComponent<CustomerHighlightsBeyondProps> = ({
  redirectUrl,
  handleCardClick,
  brand,
  ratingCount,
  score,
  showBottomCta,
  chosenBy,
  customerHighlights: reviews,
  cardPlacement,
  ...attributes
}) => {
  const {locale, language, domain, deviceType} = useRouteLoaderData('root') as RootLoaderResponse
  const translations = getTranslation(language)
  const {domainType, styles: stylesBydomain, domainName} = getDomainConfig(domain, locale)
  const isInsiderDomain = domainType === 'insider'
  const [collapsedIndexes, setCollapsedIndexes] = useState<number[]>([])
  const [firstRef, firstInView] = useInView({threshold: 1})
  const [lastRef, lastInView] = useInView({threshold: 0.9})
  const [hasScrolled, setHasScrolled] = useState(false)
  const [isReviewsCollapsed, setIsReviewsCollapsed] = useState(false)
  const attributesRef = useRef<HTMLDivElement>(null)
  const carouselRef = useRef<HTMLDivElement>(null)
  const cardColumnRef = useRef<HTMLDivElement>(null)

  const cardRefs = useRef<{[key: number]: HTMLDivElement | null}>({})

  const reportCustomEvent = useReportCustomEvent()
  const reportWidgetClick = useReportWidgetClick()

  const [widgetRefForReport] = useReportWidgetSeen({
    widget_name: 'customer-insights',
    widget_place: `card-${cardPlacement}`,
  })

  const formatter = new Intl.NumberFormat(language, {})

  const handleExpandAllReviewsClick = () => {
    setIsReviewsCollapsed((prev) => !prev)
    if (attributesRef.current && isReviewsCollapsed && deviceType !== 'desktop') {
      attributesRef.current.scrollIntoView({block: 'start'})
    }
    reportWidgetClick({
      widget_name: 'customer-insights-reviews',
      trigger_type: isReviewsCollapsed ? 'view-less' : 'view-more',
      widget_place: 'customer-insights',
    })
  }

  const handleExpandClick = (highlightIndex: number, reviewTitleAttribute: string) => {
    setCollapsedIndexes((prev) =>
      prev.includes(highlightIndex) ? prev.filter((index) => index !== highlightIndex) : [...prev, highlightIndex],
    )
    reportWidgetClick({
      widget_name: 'customer-insights-review-card',
      trigger_type: collapsedIndexes.includes(highlightIndex) ? 'less-on-this-topic' : 'more-on-this-topic',
      widget_place: `review-card-${highlightIndex + 1}`,
      variation: reviewTitleAttribute,
    })
    if (cardRefs.current[highlightIndex] && collapsedIndexes.includes(highlightIndex) && deviceType !== 'desktop') {
      cardRefs.current[highlightIndex]?.scrollIntoView({block: 'center'})
    }
  }

  const handleScroll = (direction: 'back' | 'forward') => {
    if (!carouselRef.current) return
    const cardColumnWidth = cardColumnRef?.current?.getBoundingClientRect().width ?? 0
    const cardsGap = 20
    const cardsInContainer = 3
    const container = carouselRef.current
    const scrollAmount = (cardColumnWidth + cardsGap) * cardsInContainer

    container.scrollBy({
      left: direction === 'back' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    })
  }

  const customerHighlights = reviews?.filter(({quotes}) => !!quotes?.length)
  const top3headerSpecs = customerHighlights?.slice(0, 3)

  useEffect(() => {
    const container = carouselRef.current
    if (!container || hasScrolled) return

    const handleScroll = () => {
      reportCustomEvent('scroll', {
        elementName: 'customer-insights',
      })
      setHasScrolled(true)
    }

    container.addEventListener('scroll', handleScroll)

    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [hasScrolled, reportCustomEvent, cardPlacement])

  return customerHighlights && !!customerHighlights.length ? (
    <div {...attributes} ref={attributesRef}>
      <div className="flex flex-col gap-5" ref={widgetRefForReport}>
        <div className="flex flex-row gap-14 tablet:justify-between">
          {score ? (
            <div className="flex flex-col gap-1">
              <span className="text-base font-light text-neutral-400">
                {translations.ProductCard.customerHighlights.rating}
              </span>
              <div className="flex items-center justify-center gap-2 text-5xl font-bold text-[#363636]">
                {score} <FeedbackStar className="size-7 fill-yellow-450" />
              </div>
            </div>
          ) : null}
          {chosenBy && chosenBy > 0 ? (
            <div className="flex flex-col">
              <span className="text-base font-light text-neutral-400">
                {translations.ProductCard.customerHighlights.chosenBy}
              </span>
              <span className="flex items-center gap-2 text-2xl font-bold text-[#363636]">
                {formatter.format(chosenBy)}
              </span>
            </div>
          ) : null}
        </div>
        <div className="flex flex-row items-start gap-10 tablet:flex-col tablet:gap-2">
          {top3headerSpecs?.map(({name}, index) => (
            <Fragment key={`${name}-${index}`}>
              <div className="flex flex-shrink-0 flex-row items-center justify-center gap-2">
                <ThumbUpIcon />
                <span className="text-base font-light text-[#363636]">{name}</span>
              </div>
            </Fragment>
          ))}
        </div>
        {ratingCount && ratingCount > 0 ? (
          <h2 className="mt-3 text-2xl font-semibold text-[#363636]">
            {translations.ProductCard.customerHighlights.reviewsTitle}
            <br className="desktop:hidden" />
            <em className="not-italic">{translations.ProductCard.customerHighlights.reviewsCount(ratingCount)}</em>
          </h2>
        ) : null}
      </div>
      <div className="relative">
        <div
          className={clsx(
            'pointer-events-none absolute left-0 top-0 z-10 h-full w-10 bg-gradient-to-r from-white tablet:hidden',
            (customerHighlights?.length < 3 || firstInView) && 'hidden',
          )}
        />
        <button
          type="button"
          data-testid="scroll-right-button"
          onClick={() => handleScroll('forward')}
          disabled={customerHighlights?.length < 3 || lastInView}
          aria-label="scroll right"
          className={clsx(
            customerHighlights?.length < (isInsiderDomain ? 5 : 4) && 'hidden',
            'absolute -right-6 top-28 z-30 flex size-8 items-center justify-center rounded-full border border-[#363636] bg-[#363636] stroke-[0.01em] shadow-md [&>svg>path]:fill-white',
            'disabled:border-[#C2C2C2] disabled:bg-white tablet:hidden [&>svg>path]:disabled:fill-gray-300',
          )}
        >
          <IconChevronRightThick className="size-3.5 fill-gray-500 transition-all duration-300 ease-in-out" />
        </button>
        <button
          type="button"
          data-testid="scroll-right-button"
          onClick={() => handleScroll('back')}
          disabled={customerHighlights?.length < 3 || firstInView}
          aria-label="scroll right"
          className={clsx(
            customerHighlights?.length < (isInsiderDomain ? 5 : 4) && 'hidden',
            'absolute -left-4 top-28 z-30 flex size-8 items-center justify-center rounded-full border border-[#363636] bg-[#363636] stroke-[0.01em] shadow-md [&>svg>path]:fill-white',
            'disabled:border-[#C2C2C2] disabled:bg-white tablet:hidden [&>svg>path]:disabled:fill-gray-300',
          )}
        >
          <IconChevronRightThick className="size-3.5 rotate-180 fill-gray-500 transition-all duration-300 ease-in-out" />
        </button>

        <div
          className="no-scrollbar relative flex flex-row justify-start gap-5 overflow-x-scroll pb-5 tablet:flex-col tablet:gap-0 tablet:overflow-hidden"
          style={{
            height: deviceType !== 'desktop' && !isReviewsCollapsed ? '300px' : 'auto',
          }}
          ref={carouselRef}
        >
          {customerHighlights.map((reviewsByTag, reviewsByTagIndex) => {
            const isCollapsed = collapsedIndexes.includes(reviewsByTagIndex)
            const reviewsContent = reviewsByTag.quotes.slice(0, isCollapsed ? undefined : 1)

            return (
              <Fragment key={`${reviewsByTag.name}-${reviewsByTagIndex}`}>
                <div className="flex flex-col gap-2" ref={cardColumnRef}>
                  <div
                    className="h-2"
                    ref={(el) => {
                      cardRefs.current[reviewsByTagIndex] = el
                      if (reviewsByTagIndex === 0) firstRef(el)
                      if (reviewsByTagIndex === customerHighlights.length - 1) lastRef(el)
                    }}
                  />

                  {reviewsContent?.map((review, i) => {
                    const showMoreButton = reviewsByTag.quotes.length > 1 && i === reviewsContent.length - 1
                    const colorClass =
                      HIGHLIGHTS_ATTRIBUTE_COLORS[reviewsByTagIndex % HIGHLIGHTS_ATTRIBUTE_COLORS.length]
                    return (
                      <Fragment key={`d-${reviewsByTag.name}-${i}`}>
                        <div className="flex w-60 flex-col gap-2 rounded-xl border border-neutral-100 bg-[#F5F5F5] p-4 text-base font-light tablet:w-full">
                          <div
                            className={clsx(
                              'flex w-fit items-center justify-center rounded-lg px-2 py-1 text-xs font-normal',
                              colorClass,
                            )}
                          >
                            {reviewsByTag.name}
                          </div>
                          <q className="text-[#363636]">{review}</q>
                          {showMoreButton ? (
                            <button
                              type="button"
                              aria-label={translations.ProductCard.customerHighlights.openTopics(
                                collapsedIndexes.includes(reviewsByTagIndex),
                              )}
                              className="ml-auto flex flex-row items-center gap-2 text-sm font-light text-[#64748B]"
                              onClick={() => handleExpandClick(reviewsByTagIndex, reviewsByTag.name)}
                            >
                              {translations.ProductCard.customerHighlights.openTopics(
                                collapsedIndexes.includes(reviewsByTagIndex),
                              )}
                              <IconChevronRightThick
                                className={clsx(
                                  'size-4 fill-[#64748B] transition-all duration-300 ease-in-out',
                                  isCollapsed ? '-rotate-90' : 'rotate-90',
                                )}
                              />
                            </button>
                          ) : null}
                        </div>
                      </Fragment>
                    )
                  })}
                </div>
              </Fragment>
            )
          })}
        </div>
        <div
          className={clsx(
            'pointer-events-none absolute h-full w-10 bg-gradient-to-l from-white desktop:right-0 desktop:top-0 tablet:bottom-0 tablet:h-8 tablet:w-full tablet:bg-gradient-to-t',
            (lastInView || customerHighlights?.length < 3) && 'desktop:hidden',
          )}
        />
      </div>
      <button
        type="button"
        className={clsx(
          'mx-auto my-6 hidden flex-row items-center gap-3 tablet:flex',
          stylesBydomain.LISTPAGE_BUTTON_NEW_LAYOUT_LINK,
          'listpage_button_new_layout_link',
        )}
        onClick={handleExpandAllReviewsClick}
      >
        {translations.ProductCard.customerHighlights.openReviews(isReviewsCollapsed)}
        <IconChevronRightThick
          className={clsx(
            'size-4 transition-all duration-300 ease-in-out',
            isReviewsCollapsed ? '-rotate-90' : 'rotate-90',
            stylesBydomain.LISTPAGE_BUTTON_NEW_LAYOUT_ICON_FILL,
            'listpage_button_new_layout_icon_fill',
          )}
        />
      </button>
      {showBottomCta ? (
        <div className="mx-auto flex w-full flex-col gap-3">
          <a
            href={redirectUrl}
            onClick={handleCardClick}
            aria-label={`view ${brand}`}
            target="_blank"
            rel="sponsored noreferrer"
            className={clsx(
              'relative mx-auto hidden h-[50px] w-75 flex-row items-center justify-center gap-8 rounded-3xl px-3 tablet:flex tablet:w-full',
              stylesBydomain.LISTPAGE_BUTTON_NEW_LAYOUT_PRIMARY,
              'tablet:h-11',
              'listpage_button_new_layout_primary',
            )}
          >
            <div
              className={clsx(
                'absolute left-3 top-[13px] h-[25px] shrink-0 items-center justify-start rounded-2xl px-2 py-0.5',
                stylesBydomain.LISTPAGE_BADGE_CTA_MARKETPLACE_LOGO_BG,
                stylesBydomain.LISTPAGE_BADGE_CTA_MARKETPLACE_LOGO_ICON,
                stylesBydomain.LISTPAGE_BADGE_CTA_MARKETPLACE_LOGO_TEXT,
                isInsiderDomain ? 'tablet:hidden' : 'tablet:top-2.5',
                'listpage_badge_cta_marketplace_logo_bg listpage_badge_cta_marketplace_logo_text listpage_badge_cta_marketplace_logo_icon',
              )}
            >
              <AmazonLogoIcon
                className={clsx(
                  'mt-1 h-4 shrink-0',
                  domainName === 'bestestaubsauger' ||
                    (domainName === 'bestexpertschoice' && locale.toLowerCase() === 'ca')
                    ? 'w-14'
                    : 'w-11',
                )}
                domainName={domainName}
              />
            </div>
            <p className="text-base font-semibold capitalize">{translations.ProductCard.checkPrice}</p>
          </a>
        </div>
      ) : null}
    </div>
  ) : null
}

export default CustomerHighlightsBeyond
